import { useJobillaOrgId } from '@/hooks'
import {
	customFontIndexQueryKey,
	type FontData,
	useCustomFontDestroy,
	useCustomFontIndex,
	useCustomFontUpload,
} from '@jobilla/nexus-sdk'
import { useQueryClient } from '@tanstack/react-query'
import { useAtom } from 'jotai'
import { type ChangeEvent, useCallback, useMemo, useRef, useState } from 'react'

import { trackCustomFontUploaded } from '@/ui'
import {
	Button,
	FontCard,
	FontCardPlaceholder,
	LibraryLayout,
	type PendingFontUpload,
	Tooltip,
	useUnhandledError,
} from '../../../ui'
import { fontPickerSelectedFontIdAtom } from '../atoms'
import { useFontPickerStore } from '../store'

let nextTempId = 0

export interface FontGridProps {
	mode: 'picker' | 'page'
	showCloseButton?: boolean
	onClose?: () => void
	onSelect?: (font: FontData) => void
	committalBar?: React.ReactNode | false
}

export function FontGrid({ mode, showCloseButton = false, onClose, onSelect, committalBar }: FontGridProps) {
	const store = useFontPickerStore()
	const organizationId = useJobillaOrgId()
	const { triggerError } = useUnhandledError()
	const queryClient = useQueryClient()

	const { data: fontsRaw, isLoading } = useCustomFontIndex(organizationId)
	const fonts = useMemo(() => fontsRaw?.slice().reverse(), [fontsRaw])

	const duplicateNames = useMemo(() => {
		if (!fonts) { return new Set<string>() }
		const counts = new Map<string, number>()
		for (const font of fonts) {
			counts.set(font.displayName, (counts.get(font.displayName) ?? 0) + 1)
		}
		const dupes = new Set<string>()
		for (const [name, count] of counts) {
			if (count > 1) { dupes.add(name) }
		}
		return dupes
	}, [fonts])

	const invalidateFonts = useCallback(() => {
		void queryClient.invalidateQueries({
			queryKey: customFontIndexQueryKey(organizationId),
		})
	}, [queryClient, organizationId])

	const [selectedFontId, setSelectedFontId] = useAtom(fontPickerSelectedFontIdAtom, { store })
	const inputRef = useRef<HTMLInputElement>(null)
	const [pendingUploads, setPendingUploads] = useState<PendingFontUpload[]>([])

	const uploadMutation = useCustomFontUpload()

	const destroyMutation = useCustomFontDestroy({
		onSuccess: () => {
			setSelectedFontId(null)
			invalidateFonts()
		},
		onError: (error) => {
			triggerError('Failed to delete font', undefined, { cause: error })
		},
	})

	const handleFileUpload = async (e: ChangeEvent<HTMLInputElement>) => {
		const files = Array.from(e.target.files ?? [])
		if (files.length === 0 || !organizationId) { return }

		if (inputRef.current) { inputRef.current.value = '' }

		const uploads: PendingFontUpload[] = files.map((file) => ({
			tempId: `pending-${++nextTempId}`,
			fileName: file.name,
			status: 'uploading' as const,
		}))

		setPendingUploads((prev) => [...uploads, ...prev])

		await Promise.allSettled(
			files.map(async (file, index) => {
				const tempId = uploads[index].tempId
				try {
					const uploadedFont = await uploadMutation.mutateAsync({
						organisation: organizationId,
						data: { media: file },
					})
					trackCustomFontUploaded(organizationId, uploadedFont.displayName)
					await queryClient.invalidateQueries({
						queryKey: customFontIndexQueryKey(organizationId),
					})
					setPendingUploads((prev) => prev.filter((u) => u.tempId !== tempId))
				} catch {
					setPendingUploads((prev) =>
						prev.map((u) =>
							u.tempId === tempId
								? { ...u, status: 'error' as const, error: `Failed to upload "${file.name}"` }
								: u
						)
					)
				}
			}),
		)
	}

	const handleDismissError = (tempId: string) => {
		setPendingUploads((prev) => prev.filter((u) => u.tempId !== tempId))
	}

	const handleDelete = (font: FontData) => {
		destroyMutation.mutate({
			organisation: organizationId,
			media: font.id,
		})
	}

	const hasAnyUploading = pendingUploads.some((u) => u.status === 'uploading')
	const isEmpty = (fonts?.length ?? 0) === 0 && pendingUploads.length === 0

	return (
		<LibraryLayout
			title="Custom Fonts"
			showCloseButton={showCloseButton}
			onClose={onClose}
			isLoading={isLoading}
			toolbar={
				<div className="flex items-center gap-4 px-4">
					<div className="grow-1" />
					<span className="text-[11px] text-grey-400 text-right leading-tight">
						Only upload fonts you have a license to use.
					</span>
					<Tooltip
						content={
							<div className="text-center">
								<div>Upload font files</div>
								<div className="text-[10px] text-grey-500 mt-0.5">
									WOFF2, WOFF, TTF, OTF
								</div>
							</div>
						}
					>
						<Button
							size="short"
							type="committal"
							prefix="upload"
							onClick={() => inputRef.current?.click()}
							isLoading={hasAnyUploading}
						>
							Upload
						</Button>
					</Tooltip>
					<input
						ref={inputRef}
						type="file"
						accept=".woff2,.woff,.ttf,.otf"
						multiple={true}
						hidden={true}
						onChange={handleFileUpload}
					/>
				</div>
			}
			committalBar={committalBar}
		>
			<div className="grid grid-cols-[repeat(auto-fill,minmax(250px,1fr))] gap-6 p-8 min-h-[200px]">
				{pendingUploads.map((upload) => (
					<FontCardPlaceholder
						key={upload.tempId}
						upload={upload}
						onDismiss={upload.status === 'error' ? () => handleDismissError(upload.tempId) : undefined}
					/>
				))}
				{fonts?.map((font) => (
					<FontCard
						key={font.id}
						font={font}
						mode={mode}
						isSelected={selectedFontId === font.id}
						allowDelete={true}
						hasDuplicateName={duplicateNames.has(font.displayName)}
						onSelect={() => {
							setSelectedFontId(font.id)
							onSelect?.(font)
						}}
						onDelete={() => handleDelete(font)}
					/>
				))}
				{isEmpty
					? (
						<div className="col-span-full flex items-center justify-center text-grey-400 text-sm py-16">
							No custom fonts uploaded yet. Use the upload button above to add fonts.
						</div>
					)
					: null}
			</div>
		</LibraryLayout>
	)
}
